import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { AlertCircle, Check, Loader2, Send } from "lucide-react";
import { FORMS, validate } from "../../lib/forms";
import { WHATSAPP_URL } from "../../lib/constants";
import { trackWhatsAppClick } from "../../lib/track";

/**
 * The pieces every form on the site is built from, in the look InterestForm
 * and PerformForm already had: uppercase gold labels, rounded fields, the gold
 * pill submit. Two tones — "light" on cream/ivory, "dark" on #0F0A07/#120C08.
 *
 * Behaviour, the same for every form:
 *   - Validation runs on submit and inline as each field is left; errors are
 *     written next to the field, tied to it with aria-describedby, and a
 *     summary is announced to screen readers. Focus moves to the first problem.
 *     The browser's own bubbles are off (noValidate) — they cannot be styled
 *     or worded.
 *   - Spam: a honeypot ("bot-field", which Netlify itself also drops) and a
 *     timing check. A submission within MIN_FILL_MS of the form appearing is
 *     shown the thank-you and never sent. No CAPTCHA.
 *   - While sending the button is disabled and says so. Success shows the
 *     thank-you in place, with no navigation. Failure keeps every value and
 *     says what to do.
 */

const MIN_FILL_MS = 3000;

const ToneCtx = createContext("light");
export const useTone = () => useContext(ToneCtx);

function cls(tone) {
  const dark = tone === "dark";
  return {
    label: `block font-body text-xs tracking-[0.14em] uppercase mb-2 ${dark ? "text-[#C9A961]" : "text-amber"}`,
    // scroll-mt: when focus jumps to a field with a problem, the browser
    // scrolls it to clear the fixed nav instead of tucking it under it.
    field:
      "w-full rounded-xl px-4 py-3 font-body text-base outline-none transition border scroll-mt-32 " +
      "focus-visible:ring-2 focus-visible:ring-offset-0 " +
      (dark
        ? "bg-[#1A1410] border-[#C9A961]/35 text-[#F5EFE6] placeholder:text-[#E8D9C5]/40 focus:border-[#C9A961] focus-visible:ring-[#E5C880]/45 disabled:opacity-50"
        : "bg-white border-[#C9A961]/40 text-[#2A1F17] placeholder:text-[#4A3826]/40 focus:border-[#C9A961] focus-visible:ring-[#C9A961]/40 disabled:opacity-50"),
    invalid: dark ? "border-[#F0A8A8]" : "border-[#B4322F]",
    hint: `mt-1.5 font-body text-xs leading-relaxed ${dark ? "text-[#E8D9C5]/60" : "text-[#4A3826]/70"}`,
    error: dark ? "#F0A8A8" : "#B4322F",
    text: dark ? "#E8D9C5" : "#4A3826",
    heading: dark ? "#FBF6EE" : "#2A1F17",
    panel: dark ? "border-[#C9A961]/35 bg-[#1A1410]" : "border-[#C9A961]/30 bg-[#FBF6EE]",
    choice:
      "flex items-center gap-3 rounded-xl border px-4 py-3 font-body text-[15px] cursor-pointer transition " +
      "has-[:focus-visible]:ring-2 " +
      (dark
        ? "border-[#C9A961]/30 bg-[#1A1410] text-[#F5EFE6] has-[:checked]:border-[#E5C880] has-[:checked]:bg-[#2A1F17] has-[:focus-visible]:ring-[#E5C880]/45"
        : "border-[#C9A961]/40 bg-white text-[#2A1F17] has-[:checked]:border-[#B8923E] has-[:checked]:bg-[#FBF6EE] has-[:focus-visible]:ring-[#C9A961]/40"),
    accent: dark ? "#E5C880" : "#B8923E",
  };
}

/**
 * State and submission for one form. `name` is a key of FORMS in lib/forms.js;
 * exactly its fields are posted.
 */
export function useSiteForm(name, { initial = {}, rules = {}, onSent } = {}) {
  const fields = FORMS[name].fields;
  const blank = Object.fromEntries(fields.map((f) => [f, ""]));
  const [values, setValues] = useState({ ...blank, ...initial });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [summary, setSummary] = useState("");
  const [botField, setBotField] = useState("");
  const shownAt = useRef(0);
  useEffect(() => { shownAt.current = Date.now(); }, []);

  const setValue = (k, v) => {
    setValues((s) => ({ ...s, [k]: v }));
    // Once a field has been flagged, re-check it as it is corrected, so the
    // message goes away the moment it is no longer true.
    if (errors[k]) setErrors((e) => ({ ...e, [k]: validate({ [k]: rules[k] || [] }, { [k]: v })[k] }));
  };
  const blur = (k) => {
    if (!rules[k]) return;
    const v = values[k];
    if (Array.isArray(v) ? !v.length : !String(v ?? "").trim()) return; // don't scold an untouched field
    setErrors((e) => ({ ...e, [k]: validate({ [k]: rules[k] }, values)[k] }));
  };

  async function onSubmit(e) {
    e.preventDefault();
    if (status === "sending") return;
    const found = validate(rules, values);
    setErrors(found);
    const bad = fields.filter((f) => found[f]);
    if (bad.length) {
      setSummary(
        bad.length === 1
          ? "One thing needs a look before this can go."
          : `${bad.length} things need a look before this can go.`
      );
      const el = document.getElementById(`${name}-${bad[0]}`) || document.querySelector(`[name="${bad[0]}"]`);
      if (el) el.focus();
      return;
    }
    setSummary("");

    // Bots: filled the invisible field, or submitted faster than a person can
    // read the form. Show them the same thank-you a person gets, send nothing.
    if (botField || Date.now() - shownAt.current < MIN_FILL_MS) {
      setStatus("done");
      return;
    }

    setStatus("sending");
    try {
      const body = new URLSearchParams({ "form-name": name, "bot-field": "" });
      for (const f of fields) {
        const v = values[f];
        body.append(f, Array.isArray(v) ? v.join(", ") : typeof v === "boolean" ? (v ? "yes" : "") : String(v ?? "").trim());
      }
      const res = await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (onSent) onSent(values);
      setStatus("done");
    } catch (_) {
      // Never pretend a dropped submission went through. Values stay as typed.
      setStatus("error");
    }
  }

  return { name, values, setValue, blur, errors, status, summary, onSubmit, botField, setBotField };
}

/** <form> wrapper: tone, honeypot, error summary, Netlify attributes. */
export function SiteForm({ form, tone = "light", className = "", children, testid }) {
  return (
    <ToneCtx.Provider value={tone}>
      <form
        onSubmit={form.onSubmit}
        noValidate
        name={form.name}
        method="POST"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        className={className}
        data-testid={testid || `${form.name}-form`}
        aria-busy={form.status === "sending"}
      >
        <input type="hidden" name="form-name" value={form.name} />
        {/* Honeypot — off-screen, not display:none, so bots still fill it. A
            person never reaches it: no tab stop, hidden from assistive tech. */}
        <p className="absolute left-[-9999px] w-px h-px overflow-hidden" aria-hidden="true">
          <label>
            Leave this empty
            <input
              type="text" name="bot-field" tabIndex={-1} autoComplete="off"
              value={form.botField} onChange={(e) => form.setBotField(e.target.value)}
            />
          </label>
        </p>
        {/* Announced the moment it changes; empty otherwise. */}
        <div role="alert" aria-live="assertive" className="empty:hidden">
          {form.summary && <ErrorLine tone={tone}>{form.summary}</ErrorLine>}
        </div>
        {children}
      </form>
    </ToneCtx.Provider>
  );
}

function ErrorLine({ tone, children, id }) {
  return (
    <p id={id} className="flex items-start gap-2 font-body text-sm" style={{ color: cls(tone).error }}>
      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

/** "Fields marked * are required." */
export function RequiredNote() {
  const c = cls(useTone());
  return (
    <p className="font-body text-xs" style={{ color: c.text }}>
      Fields marked <span style={{ color: c.accent }} aria-hidden="true">*</span>
      <span className="sr-only">with an asterisk</span> are required.
    </p>
  );
}

function LabelText({ label, required, optional }) {
  const c = cls(useTone());
  return (
    <>
      {label}
      {required && (
        <>
          <span aria-hidden="true" style={{ color: c.accent }}> *</span>
          <span className="sr-only"> (required)</span>
        </>
      )}
      {optional && <span className="normal-case tracking-normal opacity-60"> (optional)</span>}
    </>
  );
}

function describedBy(id, hint, error) {
  return [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined;
}

/**
 * One labelled control. `as` is "input" | "select" | "textarea"; anything else
 * on props goes to the control (type, inputMode, autoComplete, placeholder…).
 */
export function Field({ form, name, label, required, optional, hint, as = "input", options, className = "", ...props }) {
  const tone = useTone();
  const c = cls(tone);
  const id = `${form.name}-${name}`;
  const error = form.errors[name];
  const shared = {
    id,
    name,
    value: form.values[name] ?? "",
    onChange: (e) => form.setValue(name, e.target.value),
    onBlur: () => form.blur(name),
    required,
    "aria-required": required || undefined,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy(id, hint, error),
    className: `${c.field} ${error ? c.invalid : ""}`,
    "data-testid": id,
    ...props,
  };
  let control;
  if (as === "select") {
    control = (
      <select {...shared}>
        <option value="">{props.placeholder || "Choose one"}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (as === "textarea") {
    control = <textarea rows={4} {...shared} />;
  } else {
    control = <input type="text" {...shared} />;
  }
  return (
    <div className={className}>
      <label className={c.label} htmlFor={id}>
        <LabelText label={label} required={required} optional={optional} />
      </label>
      {control}
      {hint && <p id={`${id}-hint`} className={c.hint}>{hint}</p>}
      {error && <div className="mt-1.5"><ErrorLine tone={tone} id={`${id}-error`}>{error}</ErrorLine></div>}
    </div>
  );
}

/**
 * Checkboxes (multiple) or radios (single) in a fieldset with a legend.
 * Checkbox values are an array.
 */
export function Choices({ form, name, label, required, optional, hint, options, multiple = false, columns = 2 }) {
  const tone = useTone();
  const c = cls(tone);
  const id = `${form.name}-${name}`;
  const error = form.errors[name];
  const value = form.values[name];
  const picked = multiple ? (Array.isArray(value) ? value : []) : value;
  const toggle = (o) =>
    form.setValue(name, multiple ? (picked.includes(o) ? picked.filter((x) => x !== o) : [...picked, o]) : o);
  return (
    <fieldset
      className="scroll-mt-32"
      aria-describedby={describedBy(id, hint, error)}
      aria-invalid={error ? true : undefined}
      aria-required={required || undefined}
      data-testid={id}
    >
      <legend className={c.label}>
        <LabelText label={label} required={required} optional={optional} />
      </legend>
      {hint && <p id={`${id}-hint`} className={`${c.hint} -mt-1 mb-2`}>{hint}</p>}
      <div className={`grid gap-2.5 ${columns === 3 ? "grid-cols-1 min-[420px]:grid-cols-3" : "grid-cols-1 min-[420px]:grid-cols-2"}`}>
        {options.map((o, i) => (
          <label key={o} className={`${c.choice} ${error ? c.invalid : ""}`}>
            <input
              type={multiple ? "checkbox" : "radio"}
              name={name}
              value={o}
              checked={multiple ? picked.includes(o) : picked === o}
              onChange={() => toggle(o)}
              id={i === 0 ? id : undefined}
              className="h-[18px] w-[18px] flex-shrink-0 accent-[#B8923E] scroll-mt-32"
            />
            <span>{o}</span>
          </label>
        ))}
      </div>
      {error && <div className="mt-1.5"><ErrorLine tone={tone} id={`${id}-error`}>{error}</ErrorLine></div>}
    </fieldset>
  );
}

/** The gold pill, disabled and spinning while the form sends. */
export function Submit({ form, children }) {
  const sending = form.status === "sending";
  return (
    <button
      type="submit"
      disabled={sending}
      aria-disabled={sending}
      className="cta-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#E5C880]"
      data-testid={`${form.name}-submit`}
    >
      {sending ? <Loader2 size={17} className="animate-spin" aria-hidden="true" /> : <Send size={17} aria-hidden="true" />}
      <span>{sending ? "Sending…" : children}</span>
    </button>
  );
}

/** Shown when the POST fails. The values are still in the fields. */
export function SendError({ form }) {
  const tone = useTone();
  if (form.status !== "error") return null;
  return (
    <div role="alert" data-testid={`${form.name}-error`}>
      <ErrorLine tone={tone}>
        That didn't reach us — your answers are still here, so just press the button again. If it keeps failing,{" "}
        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={trackWhatsAppClick} className="underline underline-offset-4">
          message us on WhatsApp
        </a>.
      </ErrorLine>
    </div>
  );
}

/** The thank-you that replaces the form. Takes focus so it is announced. */
export function ThankYou({ form, tone = "light", title, children }) {
  const c = cls(tone);
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.focus(); }, []);
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="status"
      className={`max-w-xl mx-auto text-center rounded-2xl border p-8 md:p-10 outline-none ${c.panel}`}
      data-testid={`${form.name}-done`}
    >
      <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center bg-gradient-to-br from-[#E5C880] to-[#B8923E]">
        <Check size={26} strokeWidth={1.6} color="#2A1F17" aria-hidden="true" />
      </div>
      <h3 className="mt-5 font-display text-3xl" style={{ color: c.heading }}>{title}</h3>
      <p className="mt-3 font-body text-base leading-relaxed" style={{ color: c.text }}>{children}</p>
    </div>
  );
}
